import { useCallback, useMemo, type Ref } from 'react';
import { Box, useTheme } from '@mui/material';
import YouTubePlayer, { type YouTubePlayerHandle } from '@/components/YouTubePlayer';
import MatchEventHorizontalTimeline from '@/components/review/MatchEventHorizontalTimeline';
import VideoMagnifierOverlay from '@/components/review/VideoMagnifierOverlay';
import { useMobileLayout } from '@/hooks/useMobileLayout';
import { proModeSurfaces } from '@/utils/themeSurfaces';
import type { TimelineMarkerIcon } from '@/components/review/consensus';
import type { SessionEvent } from '@/types';

interface Props {
  playerRef: Ref<YouTubePlayerHandle>;
  videoId: string | null | undefined;
  playbackRate: number;
  initialTime?: number;
  currentTime: number;
  videoDuration: number;
  isPlaying: boolean;
  timelineEvents: SessionEvent[];
  selectedEventId?: string | null;
  educationMode?: boolean;
  onTimeUpdate: (seconds: number) => void;
  onDurationReady: (seconds: number) => void;
  onPlayingChange: (playing: boolean) => void;
  onSelectEvent: (event: SessionEvent) => void;
  onSeek: (seconds: number) => void;
  onTogglePlay: () => void;
  onPlaybackRateChange: (rate: number) => void;
  /** Inline card on review page; flex column in full-screen pro mode (always dark). */
  layout?: 'inline' | 'flex';
  /** Override timeline/shell surfaces (pro mode forces dark). */
  forceDark?: boolean;
  magnifierEnabled?: boolean;
  magnifierZoom?: number;
  onMagnifierZoomChange?: (zoom: number) => void;
  magnifierZoomMin?: number;
  magnifierZoomMax?: number;
  magnifierZoomStep?: number;
  timelineZoom?: number;
  onTimelineZoomChange?: (zoom: number) => void;
  resolveMarkerIcon?: (event: SessionEvent) => TimelineMarkerIcon | null;
  /** Landscape mobile pro mode — hide custom transport/timeline; use YouTube built-in controls. */
  hideBottomTimeline?: boolean;
}

export default function ReviewVideoPlayerShell({
  playerRef,
  videoId,
  playbackRate,
  initialTime = 0,
  currentTime,
  videoDuration,
  isPlaying,
  timelineEvents,
  selectedEventId,
  educationMode = false,
  onTimeUpdate,
  onDurationReady,
  onPlayingChange,
  onSelectEvent,
  onSeek,
  onTogglePlay,
  onPlaybackRateChange,
  layout = 'inline',
  forceDark = layout === 'flex',
  magnifierEnabled = false,
  magnifierZoom = 1.5,
  onMagnifierZoomChange,
  magnifierZoomMin = 1.5,
  magnifierZoomMax = 4,
  magnifierZoomStep = 0.5,
  timelineZoom = 1,
  onTimelineZoomChange,
  resolveMarkerIcon,
  hideBottomTimeline = false,
}: Props) {
  const theme = useTheme();
  const surfaces = useMemo(() => proModeSurfaces(theme, { forceDark }), [theme, forceDark]);
  const { isMobile, isPortraitMobile, isLandscapeMobile } = useMobileLayout();
  const isFlex = layout === 'flex';

  const getSyncTime = useCallback(
    () => (typeof playerRef === 'object' && playerRef && 'current' in playerRef ? playerRef.current?.getCurrentTime() : 0) ?? 0,
    [playerRef]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(isFlex
          ? {
              flex: hideBottomTimeline ? 1 : isPortraitMobile ? '0 0 auto' : 1,
              minWidth: 0,
              minHeight: 0,
            }
          : {}),
        borderRadius: isFlex ? 0 : 2.5,
        border: isFlex ? 'none' : '1px solid',
        borderColor: surfaces.border,
        overflow: 'hidden',
        bgcolor: isFlex ? undefined : surfaces.headerBg,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          ...(isFlex
            ? {
                flex: hideBottomTimeline || isPortraitMobile ? '1 1 auto' : 1,
                minHeight: 0,
                ...(isPortraitMobile && !hideBottomTimeline
                  ? { aspectRatio: '16/9', maxHeight: '42vh' }
                  : {}),
              }
            : {
                aspectRatio: '16/9',
                ...(isPortraitMobile ? { maxHeight: '42vh' } : {}),
              }),
        }}
      >
        <VideoMagnifierOverlay
          enabled={magnifierEnabled}
          zoom={magnifierZoom}
          videoId={videoId}
          playbackRate={playbackRate}
          isPlaying={isPlaying}
          getSyncTime={getSyncTime}
          onTogglePlay={onTogglePlay}
          onZoomChange={onMagnifierZoomChange}
          zoomMin={magnifierZoomMin}
          zoomMax={magnifierZoomMax}
          zoomStep={magnifierZoomStep}
        >
          <YouTubePlayer
            ref={playerRef}
            videoId={videoId}
            playbackRate={playbackRate}
            initialTime={initialTime}
            fillContainer
            onTimeUpdate={onTimeUpdate}
            onDurationReady={onDurationReady}
            onPlayingChange={onPlayingChange}
          />
        </VideoMagnifierOverlay>
      </Box>

      {!hideBottomTimeline && (
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: surfaces.border,
          bgcolor: surfaces.timelineBg,
          ...(isFlex
            ? {
                px: isLandscapeMobile ? 1 : isMobile ? 1.25 : 2,
                py: isLandscapeMobile ? 0.75 : isMobile ? 1 : 1.5,
              }
            : {}),
        }}
      >
        <MatchEventHorizontalTimeline
          events={timelineEvents}
          videoDuration={videoDuration}
          currentTime={currentTime}
          selectedEventId={selectedEventId}
          onSelect={onSelectEvent}
          onSeek={onSeek}
          dark={surfaces.isDark}
          compact={isMobile}
          embedded
          educationMode={educationMode}
          zoomable={isFlex && !!onTimelineZoomChange}
          timelineZoom={timelineZoom}
          onTimelineZoomChange={onTimelineZoomChange}
          resolveMarkerIcon={resolveMarkerIcon}
          playerControls={{
            currentTime,
            playbackRate,
            isPlaying,
            onTogglePlay,
            onPlaybackRateChange,
          }}
        />
      </Box>
      )}
    </Box>
  );
}
